/**
 * KeycloakProvider — authentication gate for vertical-ref mode.
 *
 * Behaviour by mode:
 *  - vertical-ref + auth.enabled=true: dynamically imports keycloak-js,
 *    initialises it, and gates rendering on a successful auth check.
 *    keycloak-js is loaded via dynamic import so tier-0 / reference builds
 *    that never enter this branch tree-shake it out entirely.
 *  - All other modes, or auth.enabled=false: renders children directly,
 *    provides a no-op AuthContext.
 *
 * Domain-agnosticism: the Keycloak URL is read from config.auth.keycloak.url
 * (injected at runtime via Caddy-mounted config.json). No .do.place string
 * appears in this file. Redirect URIs derive from window.location.origin.
 *
 * Refs: marketplace-broker-c2 brief §2.6, §3 stage 3
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { BrokerConfig } from '../config/schema';

// ── Auth context shape ───────────────────────────────────────────────────────

export interface AuthUser {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
}

export interface AuthContextValue {
  authenticated: boolean;
  user: AuthUser | null;
  token: string | null;
  logout: () => void;
  hasRole: (role: string) => boolean;
}

const AUTH_CONTEXT_DEFAULT: AuthContextValue = {
  authenticated: false,
  user: null,
  token: null,
  logout: () => {},
  hasRole: () => false,
};

const AuthContext = createContext<AuthContextValue>(AUTH_CONTEXT_DEFAULT);

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}

// ── Provider ─────────────────────────────────────────────────────────────────

interface Props {
  config: BrokerConfig;
  children: ReactNode;
}

type AuthState =
  | { phase: 'idle' }
  | { phase: 'initialising' }
  | { phase: 'authenticated'; value: AuthContextValue }
  | { phase: 'error'; message: string };

export function KeycloakProvider({ config, children }: Props) {
  const needsAuth =
    config.instance.mode === 'vertical-ref' && config.auth.enabled === true;

  const [state, setState] = useState<AuthState>(
    needsAuth ? { phase: 'initialising' } : { phase: 'idle' }
  );

  useEffect(() => {
    if (!needsAuth) return;

    const kcCfg = config.auth.keycloak;
    if (!kcCfg) {
      setState({ phase: 'error', message: 'auth.keycloak config missing for vertical-ref mode' });
      return;
    }

    let cancelled = false;

    // Dynamic import — tree-shaken away in non-auth builds (tier-0, reference)
    import('keycloak-js').then(({ default: Keycloak }) => {
      if (cancelled) return;

      const kc = new Keycloak({
        url: kcCfg.url,
        realm: kcCfg.realm,
        clientId: kcCfg.clientId,
      });

      kc.init({
        onLoad: 'login-required',
        pkceMethod: 'S256',
        // Domain-agnostic: redirect back to the current origin, not a hardcoded hostname
        redirectUri: new URL('/', window.location.origin).toString(),
        checkLoginIframe: false,
      })
        .then((authenticated) => {
          if (cancelled) return;
          if (!authenticated) {
            // init with login-required should never yield false, but be safe
            kc.login({ redirectUri: new URL('/', window.location.origin).toString() });
            return;
          }

          const parsedToken = kc.tokenParsed ?? {};
          const roles: string[] = [
            ...((parsedToken.realm_access?.roles as string[]) ?? []),
          ];

          const user: AuthUser = {
            id: parsedToken.sub ?? '',
            username: (parsedToken.preferred_username as string) ?? '',
            email: (parsedToken.email as string) ?? '',
            firstName: (parsedToken.given_name as string) ?? '',
            lastName: (parsedToken.family_name as string) ?? '',
            roles,
          };

          const value: AuthContextValue = {
            authenticated: true,
            user,
            token: kc.token ?? null,
            logout: () =>
              kc.logout({
                redirectUri: new URL('/', window.location.origin).toString(),
              }),
            hasRole: (role) => roles.includes(role),
          };

          setState({ phase: 'authenticated', value });

          // Token refresh — proactive refresh 30 s before expiry
          kc.onTokenExpired = () => {
            kc.updateToken(30).catch(() => kc.logout());
          };
        })
        .catch((err) => {
          if (cancelled) return;
          console.error('[KeycloakProvider] init error', err);
          setState({ phase: 'error', message: String(err) });
        });
    });

    return () => {
      cancelled = true;
    };
  }, [needsAuth]); // eslint-disable-line react-hooks/exhaustive-deps

  // Not an auth mode — render children directly with no-op context
  if (!needsAuth) {
    return (
      <AuthContext.Provider value={AUTH_CONTEXT_DEFAULT}>
        {children}
      </AuthContext.Provider>
    );
  }

  if (state.phase === 'initialising') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-body-sm)',
          color: 'var(--fg-3)',
        }}
      >
        Authenticating…
      </div>
    );
  }

  if (state.phase === 'error') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--fs-body-sm)',
          color: 'var(--dp-crimson-600)',
        }}
      >
        Auth error: {state.message}
      </div>
    );
  }

  if (state.phase === 'authenticated') {
    return (
      <AuthContext.Provider value={state.value}>
        {children}
      </AuthContext.Provider>
    );
  }

  return null;
}
